"use client";

import { c as _c } from "react/compiler-runtime";
import IntentLink from "./IntentLink";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import type { Dictionary } from "@/dictionaries/types";
import { getFooterNav, groupNavItems } from "@/lib/navigation";
import { useAuthStore } from "@/store/useAuthStore";
import { SecGlyph } from "./ornament";
import { localePath } from "@/lib/locale-path";
import "./footer.css";

/**
 * THE v2 FOOTER.
 *
 * Navigation is derived from the same catalogue as the rail and phone menu.
 * The catalogue is the one place that knows whether a route is public or
 * protected, so a guest cannot be sent to Portfolio, Saved or Dashboard from
 * the site's public index and a new primary route cannot quietly miss a
 * footer. The small client boundary is intentional: the persisted auth store
 * rehydrates after the first frame, at which point the catalogue swaps the
 * guest set for the signed-in set without duplicating either list here.
 */
export default function FooterV2(t0) {
  const $ = _c(83);
  const {
    lang,
    dict
  } = t0;
  const isFa = lang === "fa";
  const pathname = usePathname();
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const user = useAuthStore(_temp);
  const token = useAuthStore(_temp2);
  const t2 = Boolean(user && token);
  let t3;
  if ($[2] !== dict || $[3] !== lang || $[4] !== t2) {
    t3 = getFooterNav({
      isAuthenticated: t2
    }, lang, dict);
    $[2] = dict;
    $[3] = lang;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const items = t3;
  let t4;
  if ($[6] !== items) {
    t4 = groupNavItems(items);
    $[6] = items;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const sections = t4;
  let t5;
  if ($[8] !== items) {
    t5 = items.filter(_temp3);
    $[8] = items;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const legal = t5;
  let t6;
  if ($[10] !== isFa) {
    t6 = new Intl.DateTimeFormat(isFa ? "fa-IR" : "en-US", {
      year: "numeric"
    }).format(new Date());
    $[10] = isFa;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const year = t6;
  const t7 = `/${lang}`;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SecGlyph />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t("\u0627\u0646\u200C\u0627\u0641\u200C\u062A\u06CC\u200C\u0628\u0627\u0632", "NFTBaz");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <span>{t9}</span>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7) {
    t11 = <IntentLink className="v2-foot-mark v2-focus" href={t7}>{t8}{t10}</IntentLink>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("\u0628\u0627\u0632\u0627\u0631 \u062E\u0631\u06CC\u062F \u0648 \u0641\u0631\u0648\u0634 \u0627\u0646\u200C\u0627\u0641\u200C\u062A\u06CC \u0628\u0631\u0627\u06CC \u06A9\u0627\u0631\u0628\u0631\u0627\u0646 \u0627\u06CC\u0631\u0627\u0646\u06CC. \u0622\u062B\u0627\u0631 \u0647\u0646\u0631\u0645\u0646\u062F\u0627\u0646 \u0627\u06CC\u0631\u0627\u0646\u06CC \u0648 \u06A9\u0627\u0644\u06A9\u0634\u0646\u200C\u0647\u0627\u06CC \u062C\u0647\u0627\u0646\u06CC \u0631\u0627 \u0645\u0631\u0648\u0631 \u06A9\u0646\u06CC\u062F\u060C \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0628\u06AF\u0630\u0627\u0631\u06CC\u062F\u060C \u062F\u0631 \u062D\u0631\u0627\u062C \u0634\u0631\u06A9\u062A \u06A9\u0646\u06CC\u062F \u0648 \u0627\u062B\u0631 \u062E\u0648\u062F\u062A\u0627\u0646 \u0631\u0627 \u0628\u0627 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0631\u06CC\u0627\u0644\u06CC \u0631\u0648\u06CC \u0628\u0644\u0627\u06A9\u200C\u0686\u06CC\u0646 \u0645\u0646\u062A\u0634\u0631 \u06A9\u0646\u06CC\u062F.", "An NFT marketplace built for users in Iran. Browse Iranian artists alongside global collections, place offers, join auctions, and publish your own work on chain from a rial-funded wallet.");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <p className="v2-foot-say">{t12}</p>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = `/${lang}/currencies/eth`;
  let t15;
  if ($[24] !== t) {
    t15 = t("\u0627\u062A\u0631\u06CC\u0648\u0645", "Ethereum");
    $[24] = t;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t14 || $[27] !== t15) {
    t16 = <IntentLink className="v2-chip v2-chip--net v2-focus" href={t14}>{t15}</IntentLink>;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = `/${lang}/currencies/pol`;
  let t18;
  if ($[29] !== t) {
    t18 = t("\u067E\u0627\u0644\u06CC\u06AF\u0627\u0646", "Polygon");
    $[29] = t;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t17 || $[32] !== t18) {
    t19 = <IntentLink className="v2-chip v2-chip--net v2-focus" href={t17}>{t18}</IntentLink>;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = `/${lang}/wallet`;
  let t21;
  if ($[34] !== t) {
    t21 = t("\u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0631\u06CC\u0627\u0644\u06CC", "Rial wallet");
    $[34] = t;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t20 || $[37] !== t21) {
    t22 = <IntentLink className="v2-chip v2-chip--gold v2-focus" href={t20}>{t21}</IntentLink>;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t16 || $[40] !== t19 || $[41] !== t22) {
    t23 = <div className="v2-foot-chains">{t16}{t19}{t22}</div>;
    $[39] = t16;
    $[40] = t19;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t11 || $[44] !== t13 || $[45] !== t23) {
    t24 = <div className="v2-foot-brand">{t11}{t13}{t23}</div>;
    $[43] = t11;
    $[44] = t13;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== dict || $[48] !== sections) {
    let t26;
    if ($[50] !== dict) {
      t26 = section => <nav className={`v2-foot-col${section.items.length > 6 ? " v2-foot-col--wide" : ""}`} key={section.group} aria-labelledby={`foot-${section.group}`}><h3 id={`foot-${section.group}`}>{dict.navGroups[section.group]}</h3><ul>{section.items.map(_temp4)}</ul></nav>;
      $[50] = dict;
      $[51] = t26;
    } else {
      t26 = $[51];
    }
    t25 = sections.map(t26);
    $[47] = dict;
    $[48] = sections;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[52] !== t24 || $[53] !== t25) {
    t26 = <div className="v2-foot-in">{t24}{t25}</div>;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t) {
    t27 = t("\u0627\u0646\u200C\u0627\u0641\u200C\u062A\u06CC\u200C\u0628\u0627\u0632", "NFTBaz");
    $[55] = t;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t27 || $[58] !== year) {
    t28 = <span className="v2-foot-copy">© {year} {t27}</span>;
    $[57] = t27;
    $[58] = year;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t) {
    t29 = t("\u067E\u06CC\u0648\u0646\u062F\u0647\u0627\u06CC \u062D\u0642\u0648\u0642\u06CC", "Legal links");
    $[60] = t;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== legal) {
    t30 = legal.map(_temp5);
    $[62] = legal;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const t31 = isFa ? "en" : "fa";
  let t32;
  if ($[64] !== pathname || $[65] !== t31) {
    t32 = localePath(pathname, t31);
    $[64] = pathname;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  const t33 = isFa ? "en" : "fa";
  let t34;
  if ($[67] !== t) {
    t34 = t("English", "\u0641\u0627\u0631\u0633\u06CC");
    $[67] = t;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] !== t32 || $[70] !== t33 || $[71] !== t34) {
    t35 = <IntentLink className="v2-focus" href={t32} hrefLang={t33}>{t34}</IntentLink>;
    $[69] = t32;
    $[70] = t33;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== t29 || $[74] !== t30 || $[75] !== t35) {
    t36 = <nav aria-label={t29}>{t30}{t35}</nav>;
    $[73] = t29;
    $[74] = t30;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== t28 || $[78] !== t36) {
    t37 = <div className="v2-foot-legal">{t28}{t36}</div>;
    $[77] = t28;
    $[78] = t36;
    $[79] = t37;
  } else {
    t37 = $[79];
  }
  let t38;
  if ($[80] !== t26 || $[81] !== t37) {
    t38 = <footer className="v2-footer"><div className="v2-page">{t26}{t37}</div></footer>;
    $[80] = t26;
    $[81] = t37;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  return t38;
}
function _temp5(item_1) {
  return <IntentLink className="v2-focus" href={item_1.href} key={item_1.id}>{item_1.label}</IntentLink>;
}
function _temp4(item_0) {
  return <li key={item_0.id}><IntentLink className="v2-focus" href={item_0.href}>{item_0.label}</IntentLink></li>;
}
function _temp3(item) {
  return item.group === "legal";
}
function _temp2(s_0) {
  return s_0.token;
}
function _temp(s) {
  return s.user;
}